import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
  type ComponentType,
} from "react";

import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  DownOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  PushpinOutlined,
  PushpinFilled,
  UpOutlined,
  DownOutlined as CaretDownOutlined,
  ReadOutlined,
  SyncOutlined,
  RocketOutlined,
  SettingOutlined,
  // RobotFilled, // GoBot 入口暫時隱藏（產品決策），恢復時連同 JSX 一起取消註解
  WarningOutlined,
  GlobalOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { LANGUAGE_OPTIONS, getLanguageOption } from "@constants/locale";
import NotificationBell from "@components/shared/notifications/NotificationBell";
import { BarcodeInputProvider as BarcodeInputProviderImpl } from "@contexts/operator/BarcodeInputContext";
import { useOperatorAuth } from "@contexts/operator/OperatorAuthContext";
import { useOperatorMenu } from "@contexts/operator/OperatorMenuContext";
import { useOperatorNotification } from "@contexts/operator/OperatorNotificationContext";
import {
  OnboardingProvider as OnboardingProviderImpl,
  useOnboarding,
} from "@contexts/operator/OnboardingContext"; // Added
import OnboardingDrawerImpl from "@components/operator/OnboardingDrawer"; // Added
import TutorialSelectionModalImpl from "@components/operator/TutorialSelectionModal"; // Added
import AssistantDrawerImpl from "@/components/shared/ai/AssistantDrawer";
import BroadcastButtonImpl from "@components/operator/BroadcastButton";
import OnboardingWizardImpl from "@pages/operator/Onboarding/OnboardingWizard";
import { TourProvider } from "@contexts/TourContext";
import { useOperatorData } from "@contexts/operator/OperatorDataContext";
import { BackgroundJobPanel } from "@shared/BackgroundJobManager";
import ErrorBoundary from "@shared/ErrorBoundary";
import LanguageSwitcher from "@shared/LanguageSwitcher";
import { getAssetPath } from "@utils/assetPaths";
import { getManualUrlOrHome } from "@utils/manualMapping";
import {
  Alert,
  Layout,
  Menu,
  Button,
  Space,
  Dropdown,
  Avatar,
  Tree,
  Switch,
  Spin,
  Tooltip,
  Grid,
  Drawer,
  message as antMessage,
} from "antd";
import type { MenuProps, TreeProps } from "antd";
import { useTranslation } from "react-i18next";
import {
  Outlet,
  useNavigate,
  useLocation,
  Navigate,
  Link,
  type NavigateFunction,
} from "react-router-dom";

import { notificationService } from "@services/operator/notificationService"; // New
// testJobService import retained for parity with original .jsx (was imported but unused there too)
import { testJobService } from "@services/operator/testJobService"; // New

import type { Operator } from "@contexts/operator/OperatorAuthContext";

import {
  operatorMenuConfig,
  operatorMenuMeta,
  operatorPathToKeyMap,
  createMenuItems,
  MENU_SECTIONS,
  type MenuItem as OperatorMenuItem,
} from "./menuConfig";

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;

void testJobService;

type TFunction = ReturnType<typeof useTranslation>["t"];
type AntdMenuItems = NonNullable<MenuProps["items"]>;
type AntdMenuClickInfo = Parameters<NonNullable<MenuProps["onClick"]>>[0];
type AntdTreeDropInfo = Parameters<NonNullable<TreeProps["onDrop"]>>[0];
type AntdTreeDragStartInfo = Parameters<
  NonNullable<TreeProps["onDragStart"]>
>[0];
type AntdAllowDropInfo = Parameters<NonNullable<TreeProps["allowDrop"]>>[0];

// 內部編輯樹節點型別（含本元件自訂欄位，不完全符合 antd DataNode shape）
interface TreeNode {
  key: string;
  visible: boolean;
  pinned: boolean;
  icon?: ComponentType | null;
  labelKey: string;
  isParent: boolean;
  section?: string;
  disabled: false;
  children?: TreeNode[];
  position?: number;
}

interface MenuSettingItem {
  menu_key: string;
  position: number;
  visible: boolean;
  pinned: boolean;
}

interface DragNodeRefValue {
  key: string;
  node: AntdTreeDragStartInfo["node"];
}

// 以下 .jsx import 透過 cast 接到 .tsx — 因為對應元件還沒轉 ts，TS 推導出的 props 不完整或為 any，
// 強制以 ComponentType<any> 重新包裝以保留原 runtime 行為，不影響此處型別檢查。
// 待清理項：BroadcastButton / AssistantDrawer / TutorialSelectionModal /
// OnboardingDrawer / OnboardingWizard / BarcodeInputProvider /
// OnboardingProvider 都還是 .jsx。
const BroadcastButton = BroadcastButtonImpl as ComponentType<any>;
const AssistantDrawer = AssistantDrawerImpl as ComponentType<any>;
const TutorialSelectionModal = TutorialSelectionModalImpl as ComponentType<any>;
const OnboardingDrawer = OnboardingDrawerImpl as ComponentType<any>;
const OnboardingWizard = OnboardingWizardImpl as ComponentType<any>;
const BarcodeInputProvider =
  BarcodeInputProviderImpl as ComponentType<{ children: ReactNode }>;
const OnboardingProvider =
  OnboardingProviderImpl as ComponentType<{ children: ReactNode }>;

interface OperatorHeaderProps {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  editMode: boolean;
  handleEnterEditMode: () => void;
  menuLoading: boolean;
  pendingEditMode: boolean;
  saveMenuEdit: () => void;
  saving: boolean;
  cancelMenuEdit: () => void;
  manualUrl: string;
  jobCount: number;
  hasRunningJobs: boolean;
  hasFailedJobs: boolean;
  setVisible: (visible: boolean) => void;
  notificationData: Record<string, unknown>;
  userMenuItems: AntdMenuItems;
  currentUser: Operator | null;
  t: TFunction;
  navigate: NavigateFunction;
  addJob: (jobId: string, jobName: string, optionsOrOnComplete?: unknown) => void;
  isMobile: boolean;
  onMobileMenuClick: () => void;
  handleLogout: () => void;
  openAIChat: () => void;
}

// Separate Header Component to use useOnboarding hook
const OperatorHeader: React.FC<OperatorHeaderProps> = ({
  collapsed,
  setCollapsed,
  editMode,
  handleEnterEditMode,
  menuLoading,
  pendingEditMode,
  saveMenuEdit,
  saving,
  cancelMenuEdit,
  manualUrl,
  jobCount,
  hasRunningJobs,
  hasFailedJobs,
  setVisible,
  notificationData,
  userMenuItems,
  currentUser,
  t,
  navigate,
  addJob,
  isMobile,
  onMobileMenuClick,
  handleLogout,
  openAIChat,
}) => {
  void addJob;
  const { openTutorialSelection } = useOnboarding();
  const { i18n } = useTranslation();
  const currentLanguage = getLanguageOption(i18n.language);

  const handleLanguageChange = (langKey: string) => {
    i18n.changeLanguage(langKey);
    localStorage.setItem("gowarehouse_language", langKey);
  };

  // 小螢幕用的整合式下拉選單
  const mobileMenuItems: AntdMenuItems = [
    {
      key: "language",
      icon: <GlobalOutlined style={{ color: "#d4a574" }} />,
      label: `${currentLanguage.flag} ${currentLanguage.label}`,
      children: LANGUAGE_OPTIONS.map((lang) => ({
        key: `language:${lang.key}`,
        label: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minWidth: "140px",
            }}
          >
            <span>
              {lang.flag} {lang.label}
            </span>
            {i18n.language === lang.key && (
              <CheckOutlined style={{ color: "#1890ff", marginLeft: 8 }} />
            )}
          </div>
        ),
        onClick: () => handleLanguageChange(lang.key),
      })),
    },
    { type: "divider" },
    {
      key: "manual",
      icon: <ReadOutlined style={{ color: "#d4a574" }} />,
      label: (
        <a
          href={manualUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit" }}
        >
          {t("common.manual")}
        </a>
      ),
    },
    {
      key: "jobs",
      icon: hasRunningJobs ? (
        <SyncOutlined spin style={{ color: "#d4a574" }} />
      ) : hasFailedJobs ? (
        <WarningOutlined style={{ color: "#ff4d4f" }} />
      ) : (
        <SyncOutlined style={{ color: "#d4a574" }} />
      ),
      label:
        jobCount > 0
          ? t("common.tooltip.background_jobs_with_count", { count: jobCount })
          : t("common.tooltip.background_jobs"),
      onClick: () => setVisible(true),
    },
    { type: "divider" },
    {
      key: "profile",
      icon: <UserOutlined />,
      label: (
        <Link to="/profile" style={{ color: "inherit" }}>
          {t("operator.userMenu.profile")}
        </Link>
      ),
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: t("operator.userMenu.logout"),
      danger: true,
      onClick: handleLogout,
    },
  ];

  return (
    <Header
      className="operator-header"
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0 20px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        {/* 小螢幕：漢堡按鈕開啟 Drawer */}
        {isMobile ? (
          <Button
            className="operator-toggle"
            type="text"
            icon={<MenuUnfoldOutlined />}
            onClick={onMobileMenuClick}
          />
        ) : (
          <>
            <Button
              className="operator-toggle"
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
            />
            {!editMode ? (
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={handleEnterEditMode}
                title={t("operator.menu.edit_menu")}
                disabled={collapsed || menuLoading || pendingEditMode}
                className="operator-header-edit-btn"
              />
            ) : (
              <>
                <Button
                  type="text"
                  icon={<SaveOutlined />}
                  onClick={saveMenuEdit}
                  loading={saving}
                  title={t("common.button.save")}
                  style={{ color: "#52c41a" }}
                />
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={cancelMenuEdit}
                  disabled={saving}
                  title={t("common.button.cancel")}
                  danger
                />
              </>
            )}
          </>
        )}
        <Link to="/dashboard" className="operator-logo">
          <img
            src={getAssetPath("gowarehouse_logo")}
            alt="GoWarehouse"
            className="operator-logo__image"
          />
          <span className="operator-logo__vanta">VANTA</span>
        </Link>
      </div>

      {/* 小螢幕：整合式下拉選單 */}
      {isMobile ? (
        <Space size={8}>
          <NotificationBell
            service={notificationService}
            role="operator"
            notificationData={notificationData}
          />
          <BroadcastButton />
          {/* GoBot (AI Assistant) 入口：暫時隱藏（產品決策），恢復時取消註解即可 */}
          {/* <Tooltip title={t("operator.ai_chat.title", "AI Assistant")}>
            <Button
              type="text"
              icon={<RobotFilled style={{ color: "#2ce35dff" }} />}
              onClick={openAIChat}
              className="operator-header-icon"
            />
          </Tooltip> */}
          <Dropdown
            menu={{ items: mobileMenuItems }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <Button
              type="text"
              icon={<MenuFoldOutlined />}
              className="operator-header-icon"
            />
          </Dropdown>
        </Space>
      ) : (
        <Space size={12}>
          <LanguageSwitcher />
          <div className="header-divider" />
          <Space size={4}>
            {/* GoBot (AI Assistant) 入口：暫時隱藏（產品決策），恢復時取消註解即可 */}
            {/* <Tooltip title={t("operator.ai_chat.title", "AI Assistant")}>
              <Button
                type="text"
                icon={<RobotFilled style={{ color: "#2ce35dff" }} />}
                onClick={openAIChat}
                className="operator-header-icon"
              />
            </Tooltip> */}
            <Tooltip title={t("operator.onboarding.title", "Tutorial")}>
              <Button
                type="text"
                icon={<RocketOutlined />}
                onClick={openTutorialSelection}
                className="operator-header-icon"
              />
            </Tooltip>
            <Tooltip title={t("common.manual")}>
              <a
                href={manualUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  type="text"
                  icon={<ReadOutlined />}
                  className="operator-header-icon"
                />
              </a>
            </Tooltip>
          </Space>
          <div className="header-divider" />
          <Space size={4}>
            <Tooltip
              title={
                jobCount > 0
                  ? t("common.tooltip.background_jobs_with_count", {
                    count: jobCount,
                  })
                  : t("common.tooltip.background_jobs")
              }
            >
              <Button
                type="text"
                icon={
                  hasRunningJobs ? (
                    <SyncOutlined spin style={{ color: "#1890ff" }} />
                  ) : hasFailedJobs ? (
                    <WarningOutlined style={{ color: "#ff4d4f" }} />
                  ) : (
                    <SyncOutlined />
                  )
                }
                className={`operator-header-icon ${hasRunningJobs ? "job-running" : ""}`}
                onClick={() => setVisible(true)}
                style={{ position: "relative" }}
              >
                {jobCount > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      background: "#ff4d4f",
                      color: "white",
                      borderRadius: "50%",
                      width: "18px",
                      height: "18px",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {jobCount}
                  </span>
                )}
              </Button>
            </Tooltip>
            <NotificationBell
              service={notificationService}
              role="operator"
              notificationData={notificationData}
            />
            <BroadcastButton />
          </Space>
          <div className="header-divider" />
          <Dropdown
            menu={{
              items: userMenuItems,
            }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <Button type="text" className="operator-user-button">
              <Space>
                <Avatar size="small" icon={<UserOutlined />} />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <span style={{ fontSize: "14px", fontWeight: "500" }}>
                    {currentUser?.name || currentUser?.username}
                  </span>
                </div>
                <DownOutlined />
              </Space>
            </Button>
          </Dropdown>
        </Space>
      )}
    </Header>
  );
};

const OperatorLayoutContent: React.FC = () => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobile = !screens.md; // < 768px
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState<boolean>(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [currentUser, setCurrentUser] = useState<Operator | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editTreeData, setEditTreeData] = useState<TreeNode[]>([]);
  const [editAdvancedTreeData, setEditAdvancedTreeData] = useState<TreeNode[]>(
    [],
  );
  const [saving, setSaving] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [pinnedExpanded, setPinnedExpanded] = useState<boolean>(true);
  const [advancedExpanded, setAdvancedExpanded] = useState<boolean>(false); // 進階區塊預設收合
  const [pendingEditMode, setPendingEditMode] = useState<boolean>(false);
  const [aiChatVisible, setAiChatVisible] = useState<boolean>(false);
  const dragNodeRef = useRef<DragNodeRefValue | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const menuMeta = operatorMenuMeta;
  const pathToKeyMap = operatorPathToKeyMap;
  const {
    getPinnedMenuItems,
    getOrderedMenuConfig,
    menuSettings,
    updateMenuSettings,
    loading: menuLoading,
    refreshMenuSettings,
  } = useOperatorMenu();
  const {
    jobs,
    addJob,
    removeJob,
    clearCompletedJobs,
    visible,
    setVisible,
    jobCount,
    hasRunningJobs,
    hasFailedJobs,
    apiFetch,
    notificationData,
  } = useOperatorNotification();
  const { operator, logout: authLogout } = useOperatorAuth();
  const { showOnboarding } = useOnboarding();
  const { merchants, loading: dataLoading } = useOperatorData();

  // Set current user from auth context
  useEffect(() => {
    if (operator) {
      setCurrentUser(operator);
      setLoading(false);
    }
  }, [operator]);

  // 響應式：螢幕大小變化時關閉 mobile drawer
  useEffect(() => {
    if (!isMobile && mobileDrawerVisible) {
      setMobileDrawerVisible(false);
    }
  }, [isMobile, mobileDrawerVisible]);

  const orderedMenuConfig: OperatorMenuItem[] = useMemo(() => {
    return getOrderedMenuConfig();
  }, [getOrderedMenuConfig]);

  const pinnedItems: OperatorMenuItem[] = useMemo(() => {
    return getPinnedMenuItems();
  }, [getPinnedMenuItems]);

  // 分離主要功能區和進階區塊的選單配置
  const { mainMenuConfig, advancedMenuConfig } = useMemo(() => {
    const mainItems = orderedMenuConfig.filter(
      (item) => item.section !== MENU_SECTIONS.ADVANCED,
    );
    const advancedItems = orderedMenuConfig.filter(
      (item) => item.section === MENU_SECTIONS.ADVANCED,
    );
    return { mainMenuConfig: mainItems, advancedMenuConfig: advancedItems };
  }, [orderedMenuConfig]);

  const mainMenuItems: AntdMenuItems = useMemo(() => {
    const items = createMenuItems(t, mainMenuConfig);

    // 在收合狀態下，創建一個假的母選單項包含所有 pinned items
    if (collapsed && pinnedItems.length > 0) {
      const pinnedSubmenu = {
        key: "pinned-menu",
        icon: React.createElement(PushpinFilled, {
          style: { color: "#0d3b4f" },
        }),
        label: t("operator.menu.pinned"),
        children: pinnedItems.map((item) => ({
          key: item.key,
          label: (
            <Link
              to={item.path}
              style={{ color: "inherit", textDecoration: "none", display: "block" }}
            >
              {t(item.labelKey)}
            </Link>
          ),
          icon: item.icon ? React.createElement(item.icon) : null,
        })),
      };

      return [
        pinnedSubmenu,
        { type: "divider", key: "pinned-divider" },
        ...items,
      ];
    }

    return items;
  }, [t, mainMenuConfig, collapsed, pinnedItems]);

  const advancedMenuItems: AntdMenuItems = useMemo(() => {
    return createMenuItems(t, advancedMenuConfig);
  }, [t, advancedMenuConfig]);

  // 收集進階區塊的所有 key（含子項），用於判斷 selectedKey 是否屬於進階區塊
  const advancedKeys = useMemo(() => {
    const keys = new Set<string>();
    const collect = (items: OperatorMenuItem[]) => {
      items.forEach((item) => {
        keys.add(item.key);
        if (item.children) collect(item.children);
      });
    };
    collect(advancedMenuConfig);
    return keys;
  }, [advancedMenuConfig]);

  // ==================== 樹狀結構建立與操作 ====================

  // 建立節點標題的 JSX（共用函數）
  const createNodeTitle = (
    icon: ComponentType | null | undefined,
    labelKey: string,
    visible: boolean,
    nodeKey: string,
    pinned = false,
    isParent = false,
  ) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <Space>
        {icon && React.createElement(icon)}
        <span style={{ opacity: visible ? 1 : 0.5 }}>{t(labelKey)}</span>
      </Space>
      <Space size={4} onClick={(e) => e.stopPropagation()}>
        <Switch
          size="small"
          checked={visible}
          checkedChildren={<EyeOutlined />}
          unCheckedChildren={<EyeInvisibleOutlined />}
          onChange={(checked) => handleVisibilityChange(nodeKey, checked)}
        />
        {!isParent && (
          <Switch
            size="small"
            checked={pinned}
            checkedChildren={<PushpinFilled />}
            unCheckedChildren={<PushpinOutlined />}
            onChange={() => handlePinToggle(nodeKey)}
          />
        )}
      </Space>
    </div>
  );

  // 建立可編輯的樹狀結構（已棄用，改用 buildTreeFromSettings）
  // 保留此函數以供 handleVisibilityChange 和 handlePinToggle 內部使用

  // ==================== 可見性控制 ====================

  // 通用的節點更新函數
  const updateTreeNodes = (
    nodes: TreeNode[],
    key: string,
    updateFn: (node: TreeNode, isTargetNode: boolean) => TreeNode,
  ): TreeNode[] => {
    if (!nodes || nodes.length === 0) return [];

    return nodes.map((node) => {
      if (!node || !node.key) return node;

      const isTargetNode = node.key === key;
      const updatedNode = updateFn(node, isTargetNode);

      if (node.children && node.children.length > 0) {
        updatedNode.children = updateTreeNodes(node.children, key, updateFn);
      }

      return updatedNode;
    });
  };

  // 處理可見性變更（同時更新主要和進階區塊）
  const handleVisibilityChange = (key: string, newVisible: boolean) => {
    const updateFn = (node: TreeNode, isTargetNode: boolean): TreeNode => ({
      key: node.key,
      visible: isTargetNode ? newVisible : (node.visible ?? true),
      pinned: node.pinned ?? false,
      icon: node.icon,
      labelKey: node.labelKey,
      isParent: node.isParent || false,
      section: node.section,
      disabled: false,
    });

    setEditTreeData((prev) => updateTreeNodes(prev, key, updateFn));
    setEditAdvancedTreeData((prev) => updateTreeNodes(prev, key, updateFn));
  };

  // 處理 pin 狀態變更（同時更新主要和進階區塊）
  const handlePinToggle = (key: string) => {
    const updateFn = (node: TreeNode, isTargetNode: boolean): TreeNode => ({
      key: node.key,
      visible: node.visible ?? true,
      pinned: isTargetNode ? !(node.pinned ?? false) : (node.pinned ?? false),
      icon: node.icon,
      labelKey: node.labelKey,
      isParent: node.isParent || false,
      section: node.section,
      disabled: false,
    });

    setEditTreeData((prev) => updateTreeNodes(prev, key, updateFn));
    setEditAdvancedTreeData((prev) => updateTreeNodes(prev, key, updateFn));
  };

  // ==================== 工具函數 ====================

  // 深拷貝樹狀資料
  const deepCloneTreeData = (nodes: TreeNode[] | undefined): TreeNode[] => {
    if (!nodes || !Array.isArray(nodes)) return [];
    return nodes.filter(Boolean).map((node) => {
      const cloned: TreeNode = {
        key: node.key,
        visible: node.visible,
        pinned: node.pinned ?? false,
        icon: node.icon,
        labelKey: node.labelKey,
        isParent: node.isParent || false,
        section: node.section,
        disabled: false,
      };

      if (node.children) {
        cloned.children = deepCloneTreeData(node.children);
      }

      return cloned;
    });
  };

  // ==================== 拖曳限制邏輯 ====================

  // 從 key 判斷節點層級
  const getNodeLevel = (node: { key: React.Key }): "child" | "top" => {
    return String(node.key).includes(".") ? "child" : "top";
  };

  // 從 key 判斷父選單
  const getNodeParent = (node: { key: React.Key }): string | null => {
    const k = String(node.key);
    if (!k.includes(".")) return null;
    return k.split(".")[0];
  };

  // 控制是否允許放置
  const allowDrop: NonNullable<TreeProps["allowDrop"]> = ({
    dropNode,
    dropPosition,
  }: AntdAllowDropInfo) => {
    if (!dragNodeRef.current) return false;

    const dragNode = dragNodeRef.current.node;
    const isParentDrag = !String(dragNode.key).includes(".");
    const isDropNodeParent = !String(dropNode.key).includes(".");

    // 不允許拖拽到自己
    if (dropNode.key === dragNode.key) return false;

    // 規則 1：處理放入節點內部的情況 (dropPosition === 0)
    if (dropPosition === 0) {
      // 特殊情況：子選單拖曳到自己的母選單內部 = 移動到第一個位置
      if (!isParentDrag && isDropNodeParent) {
        const dragParent = getNodeParent(dragNode);
        if (dragParent === String(dropNode.key)) {
          return true;
        }
      }
      return false;
    }

    const dragLevel = getNodeLevel(dragNode);
    const dropLevel = getNodeLevel(dropNode);

    // 規則 2：只能在同一層級移動
    if (dragLevel !== dropLevel) return false;

    // 規則 3：子選單只能在同一個母選單下移動
    if (dragLevel === "child") {
      const dragParent = getNodeParent(dragNode);
      const dropParent = getNodeParent(dropNode);
      if (dragParent !== dropParent) return false;
    }

    return true;
  };

  // ==================== 拖曳事件處理 ====================

  // 拖曳開始
  const handleDragStart = (info: AntdTreeDragStartInfo) => {
    dragNodeRef.current = {
      key: String(info.node.key),
      node: info.node,
    };
    setIsDragging(true);

    // 若拖曳對象為母選單（isParent），收合所有母選單
    if ((info.node as unknown as { isParent?: boolean }).isParent) {
      setExpandedKeys([]);
    }
  };

  // 拖曳結束（取消）
  const handleDragEnd = () => {
    setIsDragging(false);
    dragNodeRef.current = null;
  };

  // 執行拖曳操作（通用函數，接受 setTreeData 參數）
  const performDrag = (
    info: AntdTreeDropInfo,
    setTreeData: React.Dispatch<React.SetStateAction<TreeNode[]>>,
  ) => {
    const dropKey = String(info.node.key);
    const dragKey = String(info.dragNode.key);

    const dropPos = info.node.pos.split("-");
    const dropPosition =
      info.dropPosition - Number(dropPos[dropPos.length - 1]);
    const dragPos = info.dragNode.pos.split("-");

    // 不允許拖拽到自己或自己的子節點
    if (
      dragKey === dropKey ||
      dropPos.join("-").startsWith(`${dragPos.join("-")}-`)
    )
      return;

    setTreeData((prevTreeData) => {
      const loop = (
        data: TreeNode[] | undefined,
        key: string,
        callback: (node: TreeNode, index: number, arr: TreeNode[]) => void,
      ): void => {
        if (!data || !Array.isArray(data)) return;
        for (let i = 0; i < data.length; i++) {
          if (!data[i]) continue;
          if (data[i].key === key) {
            callback(data[i], i, data);
            return;
          }
          if (data[i].children) {
            loop(data[i].children, key, callback);
          }
        }
      };

      const data = deepCloneTreeData(prevTreeData);
      let dragObj: TreeNode | undefined;

      // 使用 key 來查找節點和父數組
      interface FindResult {
        node: TreeNode;
        parent: TreeNode | null;
        parentArray: TreeNode[];
        index: number;
      }
      const findNodeAndParent = (
        data: TreeNode[] | undefined,
        key: string,
        parent: TreeNode | null = null,
        parentArray: TreeNode[] | null = null,
      ): FindResult | null => {
        if (!data || !Array.isArray(data)) return null;
        for (let i = 0; i < data.length; i++) {
          if (!data[i]) continue;
          if (data[i].key === key) {
            return {
              node: data[i],
              parent,
              parentArray: parentArray || data,
              index: i,
            };
          }
          if (data[i].children) {
            const result = findNodeAndParent(
              data[i].children,
              key,
              data[i],
              data[i].children!,
            );
            if (result) return result;
          }
        }
        return null;
      };

      const targetInfo = findNodeAndParent(data, dropKey);
      const dragInfo = findNodeAndParent(data, dragKey);

      if (!targetInfo || !dragInfo) return prevTreeData;

      // 先移除被拖拽的節點
      loop(data, dragKey, (item, index, arr) => {
        arr.splice(index, 1);
        dragObj = item;
      });

      if (!dragObj) return prevTreeData;

      // 計算插入位置
      let insertIndex: number;
      let finalTargetArray: TreeNode[];

      if (dropPosition === 0) {
        // 特殊情況：放入母選單內部 = 插入到第一個位置
        const newTargetInfo = findNodeAndParent(data, dropKey);
        if (!newTargetInfo) return prevTreeData;

        // 目標陣列是母選單的 children
        if (!newTargetInfo.node.children) {
          newTargetInfo.node.children = [];
        }
        finalTargetArray = newTargetInfo.node.children;
        insertIndex = 0; // 插入到第一個位置
      } else {
        // 一般情況：插入到間隙位置
        const newTargetInfo = findNodeAndParent(data, dropKey);
        if (!newTargetInfo) return prevTreeData;

        finalTargetArray = newTargetInfo.parentArray;

        if (dropPosition === -1) {
          // 插入到目標節點上方
          insertIndex = newTargetInfo.index;
        } else {
          // 插入到目標節點下方
          insertIndex = newTargetInfo.index + 1;
        }

        // 確保索引在有效範圍內
        insertIndex = Math.max(
          0,
          Math.min(insertIndex, finalTargetArray.length),
        );
      }

      // 插入節點到新位置
      finalTargetArray.splice(insertIndex, 0, dragObj);

      return data;
    });
  };

  // 處理主要功能區拖拽排序
  const handleMainDrop = (info: AntdTreeDropInfo) => {
    setIsDragging(false);
    dragNodeRef.current = null;
    performDrag(info, setEditTreeData);
  };

  // 處理進階區塊拖拽排序
  const handleAdvancedDrop = (info: AntdTreeDropInfo) => {
    setIsDragging(false);
    dragNodeRef.current = null;
    performDrag(info, setEditAdvancedTreeData);
  };

  // ==================== 選單編輯模式 ====================

  // 從樹狀結構提取選單設定
  const extractMenuSettings = (
    nodes: TreeNode[],
    settings: MenuSettingItem[] = [],
  ): MenuSettingItem[] => {
    nodes.forEach((node, index) => {
      settings.push({
        menu_key: node.key,
        position: index,
        visible: node.visible,
        pinned: node.pinned ?? false,
      });

      if (node.children) {
        extractMenuSettings(node.children, settings);
      }
    });

    return settings;
  };

  // 建立包含所有選單項目（含隱藏的）但按用戶設置排序的配置
  // （已棄用，改用 buildTreeFromSettings，保留此註釋說明原功能）

  // 進入編輯模式 - 使用含用戶設置的配置
  // 直接使用傳入的 settings 參數來避免 stale closure 問題
  // 返回 {mainTreeData, advancedTreeData} 分開兩個區塊
  const buildTreeFromSettings = useCallback(
    (
      currentSettings: MenuSettingItem[] | null | undefined,
    ): { mainTreeData: TreeNode[]; advancedTreeData: TreeNode[] } => {
      const buildNodes = (
        items: OperatorMenuItem[],
        settingsMap: Map<string, MenuSettingItem> | null,
      ): TreeNode[] => {
        return items
          .map((item) => {
            const setting = settingsMap?.get(item.key);
            const pinnedValue = setting?.pinned === true;
            const visibleValue = setting?.visible !== false;

            const node: TreeNode = {
              key: item.key,
              visible: visibleValue,
              pinned: pinnedValue,
              icon: item.icon,
              labelKey: item.labelKey,
              isParent: item.isParent || false,
              section: item.section,
              disabled: false,
              position: setting?.position ?? 999,
            };

            if (item.children) {
              node.children = buildNodes(item.children, settingsMap);
            }

            return node;
          })
          .sort((a, b) => (a.position ?? 999) - (b.position ?? 999));
      };

      // 分離主要功能區和進階區塊
      const mainItems = operatorMenuConfig.filter(
        (item) => item.section !== MENU_SECTIONS.ADVANCED,
      );
      const advancedItems = operatorMenuConfig.filter(
        (item) => item.section === MENU_SECTIONS.ADVANCED,
      );

      if (!currentSettings || currentSettings.length === 0) {
        // 沒有設定時，使用預設配置
        return {
          mainTreeData: buildNodes(mainItems, null),
          advancedTreeData: buildNodes(advancedItems, null),
        };
      }

      // 建立選單設定的 map
      const settingsMap = new Map<string, MenuSettingItem>(
        currentSettings.map((setting) => [setting.menu_key, setting]),
      );

      return {
        mainTreeData: buildNodes(mainItems, settingsMap),
        advancedTreeData: buildNodes(advancedItems, settingsMap),
      };
    },
    [],
  );

  // 使用 useEffect 監聯 menuSettings 變化來進入編輯模式
  useEffect(() => {
    if (pendingEditMode && !menuLoading) {
      const { mainTreeData, advancedTreeData } =
        buildTreeFromSettings(menuSettings);
      setEditTreeData(mainTreeData);
      setEditAdvancedTreeData(advancedTreeData);
      setExpandedKeys([]);
      setEditMode(true);
      setPendingEditMode(false);
    }
  }, [pendingEditMode, menuLoading, menuSettings, buildTreeFromSettings]);

  const handleEnterEditMode = useCallback(async () => {
    await refreshMenuSettings();
    setPendingEditMode(true);
  }, [refreshMenuSettings]);

  // 保存編輯
  const saveMenuEdit = async () => {
    setSaving(true);
    try {
      // 合併主要功能區和進階區塊的設定
      const mainSettings = extractMenuSettings(editTreeData);
      const advancedSettings = extractMenuSettings(editAdvancedTreeData);
      const settings = [...mainSettings, ...advancedSettings];
      const result = await updateMenuSettings(settings);

      if (result.success) {
        antMessage.success(t("operator.menu.edit_success"));
        setEditMode(false);
        setEditTreeData([]);
        setEditAdvancedTreeData([]);
        setExpandedKeys([]);
        // 重新整理選單會自動觸發，因為 OperatorMenuContext 會在 updateMenuSettings 後重新載入
      } else {
        antMessage.error(result.error || t("operator.menu.edit_error"));
      }
    } catch (error) {
      console.error("[保存選單錯誤]", error);
      antMessage.error(
        (error as Error)?.message || t("operator.menu.edit_error"),
      );
    } finally {
      setSaving(false);
    }
  };

  // 取消編輯
  const cancelMenuEdit = () => {
    setEditMode(false);
    setEditTreeData([]);
    setEditAdvancedTreeData([]);
    setExpandedKeys([]);
  };

  // ==================== 導航邏輯 ====================

  // 導航邏輯
  const selectedKey = useMemo<string | null>(() => {
    const exactMatch = pathToKeyMap[location.pathname];
    if (exactMatch) {
      return exactMatch;
    }

    let currentPath = location.pathname;
    while (currentPath.length > 1) {
      const lastSlash = currentPath.lastIndexOf("/");
      if (lastSlash === -1) {
        break;
      }
      currentPath = currentPath.substring(0, lastSlash);
      if (pathToKeyMap[currentPath]) {
        return pathToKeyMap[currentPath];
      }
    }

    return null;
  }, [location.pathname, pathToKeyMap]);

  const getAncestorKeys = useCallback(
    (key: string): string[] => {
      const ancestors: string[] = [];
      let currentKey: string | undefined = key;

      while (currentKey && menuMeta[currentKey]?.parentKey) {
        const nextParent: string = menuMeta[currentKey].parentKey!;
        ancestors.push(nextParent);
        currentKey = nextParent;
      }

      return ancestors;
    },
    [menuMeta],
  );

  const handleMenuClick = ({ key, domEvent }: AntdMenuClickInfo) => {
    const item = menuMeta[key as string] as
      | (typeof menuMeta)[string] & {
        external?: boolean;
        url?: string;
        target?: string;
        path?: string;
      }
      | undefined;

    if (!item) {
      return;
    }

    // 修飾鍵或非左鍵：label 內的 <Link> 已交給瀏覽器處理（Cmd+Click 開新分頁等），
    // 不要再用 navigate() 把當前分頁也帶過去。
    const mouseEvent = domEvent as React.MouseEvent | undefined;
    if (
      mouseEvent &&
      (mouseEvent.metaKey ||
        mouseEvent.ctrlKey ||
        mouseEvent.shiftKey ||
        mouseEvent.altKey ||
        (typeof mouseEvent.button === "number" && mouseEvent.button !== 0))
    ) {
      return;
    }

    if (item.external && item.url) {
      window.open(item.url, item.target || "_blank");
      return;
    }

    if (item.path) {
      navigate(item.path);
    }
  };

  // 處理登出
  const handleLogout = async () => {
    try {
      await authLogout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // 用戶菜單
  const userMenuItems: AntdMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: (
        <Link to="/profile" style={{ color: "inherit" }}>
          {t("operator.userMenu.profile")}
        </Link>
      ),
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: t("operator.userMenu.logout"),
      danger: true,
      onClick: handleLogout,
    },
  ];

  const handleOpenChange = (keys: string[]) => {
    const latestKey = keys.find((key) => !openKeys.includes(key));
    if (latestKey) {
      setOpenKeys([latestKey]);
    } else {
      setOpenKeys([]);
    }
  };

  // 根據當前路徑自動展開選單
  useEffect(() => {
    if (collapsed) {
      setOpenKeys([]);
      if (editMode) {
        setEditMode(false);
        setEditTreeData([]);
        setExpandedKeys([]);
      }
      return;
    }

    if (!selectedKey) {
      setOpenKeys([]);
      return;
    }

    const ancestors = getAncestorKeys(selectedKey);
    setOpenKeys(ancestors);

    // 若選中項目在進階區塊，自動展開進階區塊
    if (
      advancedKeys.has(selectedKey) ||
      ancestors.some((k) => advancedKeys.has(k))
    ) {
      setAdvancedExpanded(true);
    }
  }, [collapsed, selectedKey, getAncestorKeys, editMode, advancedKeys]);

  // 自動滾動到當前選中的選單項目
  useEffect(() => {
    if (!selectedKey || collapsed) return;
    const timer = setTimeout(() => {
      const sider = document.querySelector(".operator-sider");
      const activeItem = sider?.querySelector(".ant-menu-item-selected");
      if (activeItem) {
        activeItem.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [selectedKey, collapsed]);

  // Manual 按鈕對應的目標 URL；由 <a target="_blank"> 直接渲染，
  // 讓 Cmd+Click / middle-click / 右鍵新分頁等瀏覽器原生互動都能用。
  const manualUrl = getManualUrlOrHome(location.pathname, "operator");

  // 處理 AI Chat 按鈕點擊
  const openAIChat = () => {
    setAiChatVisible(true);
  };

  // Don't render until authentication check is complete
  if (loading) {
    return null;
  }

  // 等待選單設置載入完成
  if (menuLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Space direction="vertical" align="center">
          <Spin size="large" />
          <div style={{ marginTop: 16, color: "#666" }}>
            {t("common.loading")}
          </div>
        </Space>
      </div>
    );
  }

  // antd Tree 的 treeData 結構與本元件 TreeNode（含 visible/pinned/labelKey 等自訂欄位）不完全相容；
  // 透過 unknown cast 餵給 antd 並在 titleRender 內把 nodeData 解回 TreeNode 使用。
  const treeDataForAntd = (data: TreeNode[]) =>
    data as unknown as NonNullable<TreeProps["treeData"]>;

  // 渲染選單內容（Sider 和 Drawer 共用）
  const renderMenuContent = () => (
    <>
      {/* Pinned Menu Section */}
      {pinnedItems.length > 0 && (
        <div className="operator-pinned-menu">
          <div
            className="operator-pinned-menu__header"
            onClick={() => setPinnedExpanded(!pinnedExpanded)}
            style={{ cursor: "pointer", userSelect: "none" }}
          >
            <Space size={6}>
              <PushpinFilled style={{ fontSize: "12px", color: "#0d3b4f" }} />
              <span>{t("operator.menu.pinned")}</span>
            </Space>
            {pinnedExpanded ? (
              <UpOutlined
                style={{ fontSize: "10px", color: "rgba(0, 0, 0, 0.45)" }}
              />
            ) : (
              <CaretDownOutlined
                style={{ fontSize: "10px", color: "rgba(0, 0, 0, 0.45)" }}
              />
            )}
          </div>
          {pinnedExpanded && (
            <Menu
              theme="light"
              mode="inline"
              selectedKeys={selectedKey ? [selectedKey] : []}
              items={pinnedItems.map((item) => ({
                key: item.key,
                label: (
                  <Link
                    to={item.path}
                    style={{ color: "inherit", textDecoration: "none", display: "block" }}
                  >
                    {t(item.labelKey)}
                  </Link>
                ),
              }))}
              onClick={(info) => {
                handleMenuClick(info);
                if (isMobile) setMobileDrawerVisible(false);
              }}
              className="operator-menu operator-pinned-menu__items"
              inlineIndent={12}
            />
          )}
        </div>
      )}

      {/* Main Menu - 主要功能區 */}
      <Menu
        theme="light"
        mode="inline"
        selectedKeys={selectedKey ? [selectedKey] : []}
        items={mainMenuItems}
        onClick={(info) => {
          handleMenuClick(info);
          if (isMobile) setMobileDrawerVisible(false);
        }}
        className="operator-menu"
        inlineIndent={12}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        style={{ marginTop: pinnedItems.length > 0 ? "0" : "8px" }}
      />

      {/* Advanced Menu Section - 進階區塊 */}
      {advancedMenuItems.length > 0 && (
        <div className="operator-advanced-menu">
          <div
            className="operator-advanced-menu__header"
            onClick={() => setAdvancedExpanded(!advancedExpanded)}
            style={{ cursor: "pointer", userSelect: "none" }}
          >
            <Space size={10}>
              <SettingOutlined style={{ fontSize: "16px" }} />
              <span style={{ fontWeight: 800 }}>
                {t("operator.menu.advanced")}
              </span>
            </Space>
            {advancedExpanded ? (
              <UpOutlined
                style={{ fontSize: "10px", color: "rgba(0, 0, 0, 0.45)" }}
              />
            ) : (
              <CaretDownOutlined
                style={{ fontSize: "10px", color: "rgba(0, 0, 0, 0.45)" }}
              />
            )}
          </div>
          {advancedExpanded && (
            <Menu
              theme="light"
              mode="inline"
              selectedKeys={selectedKey ? [selectedKey] : []}
              items={advancedMenuItems}
              onClick={(info) => {
                handleMenuClick(info);
                if (isMobile) setMobileDrawerVisible(false);
              }}
              className="operator-menu operator-advanced-menu__items"
              inlineIndent={12}
              openKeys={openKeys}
              onOpenChange={handleOpenChange}
            />
          )}
        </div>
      )}
    </>
  );

  return (
    <BarcodeInputProvider>
      <Layout className="operator-shell">
        {/* 小螢幕時使用 Drawer，大螢幕時使用 Sider */}
        {isMobile ? (
          <Drawer
            placement="left"
            open={mobileDrawerVisible}
            onClose={() => setMobileDrawerVisible(false)}
            width={260}
            styles={{
              header: {
                background: "#0d3b4f",
                borderBottom: "none",
                padding: "16px",
              },
              body: { padding: 0, background: "#faf8f3" },
            }}
            className="operator-mobile-drawer"
            title={
              <Space
                style={{ color: "#fff", cursor: "pointer" }}
                onClick={() => setMobileDrawerVisible(false)}
              >
                <MenuFoldOutlined />
                <span>{t("operator.menu.hide_menu", "HIDE MENU")}</span>
              </Space>
            }
            closeIcon={null}
          >
            {renderMenuContent()}
          </Drawer>
        ) : (
          <Sider
            className="operator-sider"
            trigger={null}
            collapsible
            collapsed={collapsed}
            width={editMode ? 312 : 208}
            theme="light"
          >
            {/* 選單或編輯樹 */}
            {editMode ? (
              <div className="operator-menu-edit-container">
                {/* 主要功能區編輯樹 */}
                <Tree
                  draggable={true}
                  showLine={false}
                  showIcon={false}
                  blockNode
                  expandedKeys={expandedKeys}
                  onExpand={(keys) => {
                    // 在拖曳過程中禁用展開
                    if (!isDragging) {
                      setExpandedKeys(keys);
                    }
                  }}
                  treeData={treeDataForAntd(editTreeData)}
                  titleRender={(nodeData) => {
                    const n = nodeData as unknown as TreeNode;
                    return createNodeTitle(
                      n.icon,
                      n.labelKey,
                      n.visible,
                      n.key,
                      n.pinned,
                      n.isParent,
                    );
                  }}
                  onDrop={handleMainDrop}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  allowDrop={allowDrop}
                  className={`operator-menu-edit-tree ${isDragging ? "is-dragging" : ""}`}
                  style={{ marginTop: "8px" }}
                />

                {/* 進階區塊編輯樹 */}
                {editAdvancedTreeData.length > 0 && (
                  <div className="operator-advanced-menu operator-advanced-menu--edit">
                    <div className="operator-advanced-menu__header">
                      <Space size={6}>
                        <SettingOutlined
                          style={{ fontSize: "12px", color: "#666" }}
                        />
                        <span>{t("operator.menu.advanced")}</span>
                      </Space>
                    </div>
                    <Tree
                      draggable={true}
                      showLine={false}
                      showIcon={false}
                      blockNode
                      expandedKeys={expandedKeys}
                      onExpand={(keys) => {
                        if (!isDragging) {
                          setExpandedKeys(keys);
                        }
                      }}
                      treeData={treeDataForAntd(editAdvancedTreeData)}
                      titleRender={(nodeData) => {
                        const n = nodeData as unknown as TreeNode;
                        return createNodeTitle(
                          n.icon,
                          n.labelKey,
                          n.visible,
                          n.key,
                          n.pinned,
                          n.isParent,
                        );
                      }}
                      onDrop={handleAdvancedDrop}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      allowDrop={allowDrop}
                      className={`operator-menu-edit-tree ${isDragging ? "is-dragging" : ""}`}
                    />
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Pinned Menu Section */}
                {!collapsed && pinnedItems.length > 0 && (
                  <div className="operator-pinned-menu">
                    <div
                      className="operator-pinned-menu__header"
                      onClick={() => setPinnedExpanded(!pinnedExpanded)}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      <Space size={6}>
                        <PushpinFilled
                          style={{ fontSize: "12px", color: "#0d3b4f" }}
                        />
                        <span>{t("operator.menu.pinned")}</span>
                      </Space>
                      {pinnedExpanded ? (
                        <UpOutlined
                          style={{
                            fontSize: "10px",
                            color: "rgba(0, 0, 0, 0.45)",
                          }}
                        />
                      ) : (
                        <CaretDownOutlined
                          style={{
                            fontSize: "10px",
                            color: "rgba(0, 0, 0, 0.45)",
                          }}
                        />
                      )}
                    </div>
                    {pinnedExpanded && (
                      <Menu
                        theme="light"
                        mode="inline"
                        selectedKeys={selectedKey ? [selectedKey] : []}
                        items={pinnedItems.map((item) => ({
                          key: item.key,
                          label: (
                            <Link
                              to={item.path}
                              style={{ color: "inherit", textDecoration: "none", display: "block" }}
                            >
                              {t(item.labelKey)}
                            </Link>
                          ),
                        }))}
                        onClick={handleMenuClick}
                        className="operator-menu operator-pinned-menu__items"
                        inlineIndent={12}
                      />
                    )}
                  </div>
                )}

                {/* Main Menu - 主要功能區 */}
                <Menu
                  theme="light"
                  mode="inline"
                  selectedKeys={selectedKey ? [selectedKey] : []}
                  items={mainMenuItems}
                  onClick={handleMenuClick}
                  className="operator-menu"
                  inlineCollapsed={collapsed}
                  inlineIndent={12}
                  openKeys={!collapsed ? openKeys : undefined}
                  onOpenChange={!collapsed ? handleOpenChange : undefined}
                  style={{
                    marginTop:
                      pinnedItems.length > 0 && !collapsed ? "0" : "8px",
                  }}
                />

                {/* Advanced Menu Section - 進階區塊 */}
                {!collapsed && advancedMenuItems.length > 0 && (
                  <div className="operator-advanced-menu">
                    <div
                      className="operator-advanced-menu__header"
                      onClick={() => setAdvancedExpanded(!advancedExpanded)}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      <Space size={6}>
                        <SettingOutlined
                          style={{ fontSize: "12px", color: "#666" }}
                        />
                        <span style={{ fontWeight: 800 }}>
                          {t("operator.menu.advanced")}
                        </span>
                      </Space>
                      {advancedExpanded ? (
                        <UpOutlined
                          style={{
                            fontSize: "10px",
                            color: "rgba(0, 0, 0, 0.45)",
                          }}
                        />
                      ) : (
                        <CaretDownOutlined
                          style={{
                            fontSize: "10px",
                            color: "rgba(0, 0, 0, 0.45)",
                          }}
                        />
                      )}
                    </div>
                    {advancedExpanded && (
                      <Menu
                        theme="light"
                        mode="inline"
                        selectedKeys={selectedKey ? [selectedKey] : []}
                        items={advancedMenuItems}
                        onClick={handleMenuClick}
                        className="operator-menu operator-advanced-menu__items"
                        inlineIndent={12}
                        openKeys={!collapsed ? openKeys : undefined}
                        onOpenChange={!collapsed ? handleOpenChange : undefined}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </Sider>
        )}
        <Layout className="operator-main">
          <OperatorHeader
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            editMode={editMode}
            handleEnterEditMode={handleEnterEditMode}
            menuLoading={menuLoading}
            pendingEditMode={pendingEditMode}
            saveMenuEdit={saveMenuEdit}
            saving={saving}
            cancelMenuEdit={cancelMenuEdit}
            manualUrl={manualUrl}
            jobCount={jobCount}
            hasRunningJobs={hasRunningJobs}
            hasFailedJobs={hasFailedJobs}
            setVisible={setVisible}
            notificationData={notificationData}
            userMenuItems={userMenuItems}
            currentUser={currentUser}
            t={t}
            navigate={navigate}
            addJob={addJob}
            isMobile={isMobile}
            onMobileMenuClick={() => setMobileDrawerVisible(true)}
            handleLogout={handleLogout}
            openAIChat={openAIChat}
          />
          <Content
            className="operator-content"
            style={{
              marginRight: showOnboarding ? "450px" : "0",
              transition: "margin-right 0.3s ease",
            }}
          >
            {!dataLoading && merchants.length === 0 && (
              <Alert
                message={
                  <span>
                    <strong>{t("operator.layout.no_merchants_title")}</strong>
                    {" — "}
                    {t("operator.layout.no_merchants_description")}
                  </span>
                }
                type="warning"
                showIcon
                closable
                style={{
                  borderRadius: 0,
                  borderLeft: "none",
                  borderRight: "none",
                  borderTop: "none",
                }}
              />
            )}
            <ErrorBoundary
              showDetails={
                (import.meta as unknown as { env: { DEV: boolean } }).env.DEV
              }
              homeUrl="/operator"
            >
              <Outlet />
            </ErrorBoundary>
          </Content>
        </Layout>

        {/* Onboarding Components */}
        <TutorialSelectionModal />
        <OnboardingDrawer />

        {/* AI Assistant Drawer */}
        <AssistantDrawer
          visible={aiChatVisible}
          onClose={() => setAiChatVisible(false)}
        />

        {/* Background Job Panel */}
        {/*
          NOTE: BackgroundJobManager 與 OperatorNotificationContext 各自宣告了
          不相容的 BackgroundJob interface (status: JobStatus vs status?: string)。
          Runtime 由 useBackgroundJob() 提供，shape 對齊前者，所以這裡用 jobs 的
          runtime 型別餵給 BackgroundJobPanel。待清理：把 BackgroundJobManager 的
          BackgroundJob export 出來、讓 OperatorNotificationContextValue 直接共用。
        */}
        {visible && (
          <BackgroundJobPanel
            jobs={
              jobs as unknown as React.ComponentProps<
                typeof BackgroundJobPanel
              >["jobs"]
            }
            onRemoveJob={removeJob}
            onClearCompleted={clearCompletedJobs}
            onClose={() => setVisible(false)}
            apiFetch={apiFetch}
          />
        )}
      </Layout>
    </BarcodeInputProvider>
  );
};

// Wrap OperatorLayoutContent with OnboardingProvider
const OperatorLayout: React.FC = () => {
  const { loading: dataLoading } = useOperatorData();
  const { operator } = useOperatorAuth();
  const location = useLocation();

  // 等待資料載入完成再決定顯示什麼
  if (dataLoading || !operator) {
    return null;
  }

  // 強制 onboarding：後端判定需要初始設定時（缺少 warehouse / inventory_type / merchant），攔截整個 layout
  if (operator.force_onboarding === true) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f8fafc",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 24,
        }}
      >
        <OnboardingWizard onClose={() => window.location.reload()} />
      </div>
    );
  }

  // 帳號停權 / 取消：強制導到 governance 付款頁，避免使用者在其他頁面吃 402 空白畫面
  // 例外：已經在 /governance/* 下的頁面（含 subscription / billing_profile / payment_methods / invoices）
  // 就不再攔截，讓使用者可以完成付款流程
  const accountStatus = operator.governance_account_status;
  if (
    (accountStatus === "suspended" || accountStatus === "cancelled") &&
    !location.pathname.includes("/governance/")
  ) {
    return <Navigate to="/governance/subscription" replace />;
  }

  return (
    <TourProvider>
      <OnboardingProvider>
        <OperatorLayoutContent />
      </OnboardingProvider>
    </TourProvider>
  );
};

export default OperatorLayout;
